// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

interface Point {
  date: string
  sevenDayAvgKg: number
}

interface Props {
  points: Point[]
  ariaLabel: string
}

const WIDTH = 280
const HEIGHT = 64
const PADDING = 6

/**
 * Simple inline SVG sparkline for the 7-day-average weight trend
 * (ADR-027 rule 2) — no chart library, brand green stroke/fill, matching
 * the rest of the onboarding/insights component set's hand-rolled SVG
 * icons. Renders nothing below 2 points (the calculator already only ever
 * returns points once a real 2-entry window exists, but this stays honest
 * even if called directly).
 */
export function WeightSparkline({ points, ariaLabel }: Props) {
  if (points.length < 2) return null

  const values = points.map(p => p.sevenDayAvgKg)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const stepX = (WIDTH - PADDING * 2) / (points.length - 1)

  const coords = points.map((p, i) => {
    const x = PADDING + i * stepX
    const y = PADDING + (1 - (p.sevenDayAvgKg - min) / range) * (HEIGHT - PADDING * 2)
    return { x, y }
  })

  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x},${c.y}`).join(" ")
  const areaPath = `${linePath} L${coords[coords.length - 1]!.x},${HEIGHT - PADDING} L${coords[0]!.x},${HEIGHT - PADDING} Z`

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={ariaLabel}
      className="h-16 w-full"
      preserveAspectRatio="none"
    >
      <path d={areaPath} className="fill-green/10" stroke="none" />
      <path
        d={linePath}
        className="stroke-green"
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <circle
          key={points[i]!.date}
          cx={c.x}
          cy={c.y}
          r={i === coords.length - 1 ? 3 : 1.5}
          className="fill-green"
        />
      ))}
    </svg>
  )
}
